/**
 * Floating Feedback Widget Component
 * Provides persistent feedback collection interface for the CLEAR platform
 */

import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import {
  FeedbackType,
  FeedbackWidgetConfig,
  FeedbackFormState,
  ElementSelectionState
} from '../../types/feedback';
import FeedbackModal from './FeedbackModal';
import QuickReactionPanel from './QuickReactionPanel';
import { useFeedbackService } from '../../hooks/useFeedbackService';
import { useTheme } from '../../contexts/ThemeContext';

interface FeedbackWidgetProps {
  config?: Partial<FeedbackWidgetConfig>;
  className?: string;
}

const defaultConfig: FeedbackWidgetConfig = {
  position: 'bottom-right',
  showQuickReactions: true,
  enableScreenshot: true,
  enableElementSelection: true,
  customTriggers: [],
  theme: {
    primaryColor: '#A0725C', // terracotta
    backgroundColor: '#FFFFFF',
    textColor: '#334155' // slate-700
  }
};

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  config = {},
  className
}) => {
  const { theme } = useTheme();
  const isSwissTheme = theme === 'swiss';
  const finalConfig = { ...defaultConfig, ...config };
  const [isOpen, setIsOpen] = useState(false);
  const [showQuickReactions, setShowQuickReactions] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [elementSelection, setElementSelection] = useState<ElementSelectionState>({
    isActive: false
  });

  const widgetRef = useRef<HTMLDivElement>(null);
  const { submitFeedback, isSubmitting } = useFeedbackService();

  // Position classes based on config
  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6', 
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6'
  };

  // Handle click outside to close quick reactions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target as Node)) {
        setShowQuickReactions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Alt + F to open feedback widget
      if (event.altKey && event.key === 'f') {
        event.preventDefault();
        setIsOpen(true);
      }
      
      // Escape to close widget/reactions
      if (event.key === 'Escape') {
        setIsOpen(false);
        setShowQuickReactions(false);
        setElementSelection({ isActive: false });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleWidgetClick = () => {
    if (isMinimized) {
      setIsMinimized(false);
      setShowQuickReactions(true);
    } else if (finalConfig.showQuickReactions && !showQuickReactions) {
      setShowQuickReactions(true);
    } else {
      setIsOpen(true);
      setFeedbackType('general');
    }
  };

  const handleQuickReaction = (reaction: 'helpful' | 'confusing' | 'error' | 'improvement') => {
    setFeedbackType('quick-reaction');
    setIsOpen(true);
    setShowQuickReactions(false);
  };

  const handleFeedbackTypeSelect = (type: FeedbackType) => {
    setFeedbackType(type);
    setIsOpen(true);
    setShowQuickReactions(false);
  };

  const handleModalClose = () => {
    setIsOpen(false);
    setFeedbackType(null);
    setElementSelection({ isActive: false });
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setShowQuickReactions(false);
  };

  // Element feedback - now opens modal with element-feedback type
  const startElementSelection = () => {
    setFeedbackType('element-feedback');
    setIsOpen(true);
    setShowQuickReactions(false);
  };

  if (isMinimized) {
    return (
      <div 
        ref={widgetRef}
        data-feedback-widget
        className={cn(
          'fixed z-[999999] transition-all duration-300',
          positionClasses[finalConfig.position],
          className
        )}
        style={{ zIndex: 999999 }}
      >
        <button
          onClick={handleWidgetClick}
          className={cn(
            "w-14 h-14 md:w-12 md:h-12 text-white shadow-lg hover:shadow-xl",
            "hover:scale-110 transition-all duration-300 flex items-center justify-center",
            "focus:outline-none focus:ring-2 focus:ring-offset-2 touch-manipulation",
            isSwissTheme ? "bg-blue-500 hover:bg-blue-600 focus:ring-blue-500" : "bg-warm-500 focus:ring-warm-500"
          )}
          style={{borderRadius: '50%'}}
          aria-label="Open feedback widget"
          title="Provide feedback (Alt+F)"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
        </button>
      </div>
    );
  }

  return (
    <>
      <div 
        ref={widgetRef}
        data-feedback-widget
        className={cn(
          'fixed z-[999999] transition-all duration-300',
          positionClasses[finalConfig.position],
          className
        )}
        style={{ zIndex: 999999 }}
      >
        {/* Quick Reactions Panel */}
        {showQuickReactions && finalConfig.showQuickReactions && (
          <QuickReactionPanel
            onReaction={handleQuickReaction}
            onFeedbackType={handleFeedbackTypeSelect}
            onElementSelection={finalConfig.enableElementSelection ? startElementSelection : undefined}
            onClose={() => setShowQuickReactions(false)}
            onMinimize={handleMinimize}
            position={finalConfig.position}
            theme={finalConfig.theme}
            isSwissTheme={isSwissTheme}
          />
        )}

        {/* Main Widget Button */}
        <button
          onClick={handleWidgetClick}
          className={cn(
            'group relative bg-white shadow-lg hover:shadow-xl',
            'transition-all duration-300 flex items-center gap-3 px-4 py-3 min-h-[48px] touch-manipulation',
            'focus:outline-none focus:ring-2 focus:ring-offset-2',
            showQuickReactions && 'scale-95 opacity-90',
            isSwissTheme
              ? 'border border-blue-500 focus:ring-blue-500'
              : 'border-2 border-warm-500 focus:ring-warm-500'
          )}
          style={{borderRadius: isSwissTheme ? '4px' : '50px'}}
          aria-label="Feedback options"
          title="Provide feedback (Alt+F)"
        >
          {/* Feedback Icon */}
          <div className={cn(
            "w-6 h-6 transition-transform group-hover:scale-110",
            isSwissTheme ? "text-blue-500" : "text-warm-500"
          )}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
          </div>

          {/* Text (hidden on mobile) */}
          <span className={cn(
            "hidden sm:block text-sm font-medium transition-colors",
            isSwissTheme
              ? "text-gray-700 group-hover:text-blue-500"
              : "text-neutral-700 group-hover:text-warm-500"
          )}>
            Feedback
          </span>

          {/* Pulse indicator for pending feedback */}
          {isSubmitting && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-ping" />
          )}
        </button>

        {/* Accessibility hint */}
        <div className="sr-only">
          Press Alt+F to open feedback widget, or click to provide feedback about this page
        </div>
      </div>

      {/* Feedback Modal */}
      {isOpen && (
        <FeedbackModal
          feedbackType={feedbackType}
          isOpen={isOpen}
          onClose={handleModalClose}
          onSubmit={submitFeedback}
          elementSelection={elementSelection}
          onElementSelectionChange={setElementSelection}
          config={finalConfig}
        />
      )}
    </>
  );
};

export default FeedbackWidget;